import React, { useState } from "react";
import { Button, Col, Modal, Row, Table } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

import { useFetchConstant } from "apis/constant";
import {
  constructDisplayPrice,
  constructShortenedFinancialNumber,
} from "utils/general";
import { getReportingData } from "utils/reporting";

import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";
import SummaryInfoCard from "components/SummaryInfoCard/SummaryInfoCard";
import PDFReportDownloadButton from "./components/PDFReportDownloadButton";

import {
  CoverageLabel,
  CoveragePriceText,
  CoverageYearText,
  CardContainer,
  DescriptionForLabel,
  ExtraInfoText,
  FinancialValueText,
  GhostButton,
  Label,
} from "./Reporting.styles";

import Locale from "react-intl-universal";

const getRiskRatioTableColumns = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    width: "30%",
    render: (value) => <Label>{value}</Label>,
  },
  {
    dataIndex: "value",
    key: "value",
    width: "20%",
    render: (value, rec) => (
      <FinancialValueText isInRange={rec.isInRange}>{value}</FinancialValueText>
    ),
  },
  {
    dataIndex: "range",
    key: "range",
    width: "20%",
    render: (value, record) => {
      let replaceRange = value.replace(
        /months/g,
        Locale.get("reporting.months")
      );

      return replaceRange;
    },
  },
  {
    dataIndex: "desc",
    key: "desc",
    width: "30%",
    render: (value) => <ExtraInfoText>{value}</ExtraInfoText>,
  },
];

const getGrossInsNeedsTableColumns = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    width: "70%",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const constructGrossInsNeedsDataSource = (
  data = {},
  isIncludeMortgages = false
) => {
  const dataSource = [
    {
      label: Locale.get("reporting.monthlyExpenditure"),
      desc: !isIncludeMortgages && Locale.get("reporting.expenditureDesc"),
      value: constructDisplayPrice(data.monthlyExpenditure || 0, false),
    },
    {
      label: Locale.get("reporting.annualExpenditure"),
      desc: !isIncludeMortgages && Locale.get("reporting.expenditureDesc"),
      value: constructDisplayPrice(data.annualExpenditure || 0, false),
    },
    {
      label: Locale.get("reporting.inflation"),
      value: `${data.inflation ? Number(data.inflation * 100).toFixed(2) : 0}%`,
    },
    {
      label: Locale.get("reporting.interestRate"),
      value: `${
        data.investmentInterestRate
          ? Number(data.investmentInterestRate * 100).toFixed(2)
          : 0
      }%`,
    },
    {
      label: Locale.get("reporting.eAdjustedInflation"),
      value: `${
        data.estimateAdjInflation
          ? Number(data.estimateAdjInflation * 100).toFixed(2)
          : 0
      }%`,
    },
    {
      label: Locale.get("reporting.yearCoverageBasicResponsible"),
      value: data.yearsOfCoverage || 0,
    },
    {
      label: <b>{Locale.get("reporting.tLifeInsuranceNeed")}</b>,
      value: <b>{constructDisplayPrice(data.lifeInsNeeds || 0, false)}</b>,
    },
  ];

  if (data.ide) {
    dataSource.splice(2, 0, {
      label: Locale.get("reporting.immediateDeathExpense"),
      value: constructDisplayPrice(data.ide, false),
    });
  }
  if (data.recoverRecuperatingFund) {
    dataSource.splice(6, 0, {
      label: "Recover & Recuperating Funds (RRF)",
      value: constructDisplayPrice(data.recoverRecuperatingFund || 0, false),
    });
  }
  if (data.medicalHealthInsuranceNeeds) {
    dataSource.splice(7, 0, {
      label: "Medical Health Insurance Needs",
      value: constructDisplayPrice(
        data.medicalHealthInsuranceNeeds || 0,
        false
      ),
    });
  }

  return dataSource;
};

const GrossCoverageCard = ({ data, order, title }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const tableTitle = {
    "Death Coverage": Locale.get("reporting.deathCoverageTableTitle"),
    "TPD Coverage": Locale.get("reporting.tpdCoverageTableTitle"),
    "CI Coverage": Locale.get("reporting.ciCoverageTableTitle"),
  };
  const isIncludedMortgages = title === "CI Coverage";
  return (
    <>
      <Col span={12} lg={8} order={order}>
        <CardContainer>
          <CoverageLabel>{title}</CoverageLabel>
          <CoveragePriceText>
            {data.lifeInsNeeds
              ? constructShortenedFinancialNumber(data.lifeInsNeeds, false)
              : "-"}
          </CoveragePriceText>
          <CoverageYearText>
            <b>{data.yearsOfCoverage || "-"}</b> {Locale.get("reporting.years")}
            {/* for <b>{data.yearsOfCoverage || "-"}</b> years */}
          </CoverageYearText>
          {!isModalVisible ? (
            <Button
              type="primary"
              icon={<DownOutlined />}
              onClick={() => setIsModalVisible(true)}
              style={{ position: "relative" }}
            >
              {Locale.get("reporting.knowMoreButton")}
            </Button>
          ) : (
            <GhostButton
              icon={<UpOutlined />}
              onClick={() => setIsModalVisible(false)}
            >
              {Locale.get("reporting.knowLessButton")}
            </GhostButton>
          )}
          {isModalVisible && (
            <Modal
              footer={null}
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              width="80%"
            >
              <Row style={{ marginTop: "32px" }}>
                <Table
                  dataSource={constructGrossInsNeedsDataSource(
                    data,
                    isIncludedMortgages
                  )}
                  columns={getGrossInsNeedsTableColumns(tableTitle[title])}
                  pagination={false}
                  rowKey={(record) => record.label}
                  style={{ width: "100%" }}
                />
              </Row>
            </Modal>
          )}
        </CardContainer>
      </Col>
    </>
  );
};

const Reporting = ({
  personalInfo,
  financialInfo,
  insurancesInfo,
  reportingDetails,
  personalDetails,
  cashFlowDetails,
  estatePlanningDetails,
  investmentPortfolio,
  actionPlans,
  futureReviews,
  contactList,
}) => {
  const { selection: benefitTypes } = useFetchConstant("benefitTypes");
  const { selection: investmentPlans } = useFetchConstant("investmentPlans");
  const { selection: policyStatuses } = useFetchConstant("policyStatuses");

  const { riskRatioData } = reportingDetails;
  const {
    naturalDeathCoverageData,
    accidentalDeathCoverageData,
    medicalBenefitCoverageData,
    tpdCoverageData,
    comatoseCoverageData,
  } = getReportingData(riskRatioData, {}, personalDetails);

  const isLoading =
    !benefitTypes.length || !investmentPlans.length || !policyStatuses.length;

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "32px" }}
      >
        <Title>{Locale.get("reporting.pageTitle")}</Title>
        {!isLoading && (
          <PDFReportDownloadButton
            accidentalDeathCoverageData={accidentalDeathCoverageData}
            actionPlans={actionPlans}
            benefitTypes={benefitTypes}
            policyStatuses={policyStatuses}
            cashFlowSummary={cashFlowDetails}
            comatoseCoverageData={comatoseCoverageData}
            estatePlanningDetails={estatePlanningDetails}
            financialInfo={financialInfo}
            futureReviews={futureReviews}
            insurancesInfo={insurancesInfo}
            investmentPortfolio={investmentPortfolio}
            medicalBenefitCoverageData={medicalBenefitCoverageData}
            naturalDeathCoverageData={naturalDeathCoverageData}
            personalDetails={personalDetails}
            personalInfo={personalInfo}
            reportingDetails={reportingDetails}
            tpdCoverageData={tpdCoverageData}
            contactList={contactList}
          />
        )}
      </Row>
      <SectionContainer>
        <Row gutter={[24, 16]}>
          <GrossCoverageCard
            order={1}
            data={
              reportingDetails.grossInsNeeds &&
              reportingDetails.grossInsNeeds.deathCover
            }
            title={Locale.get("reporting.deathCoverage")}
          />
          <GrossCoverageCard
            order={2}
            data={
              reportingDetails.grossInsNeeds &&
              reportingDetails.grossInsNeeds.tpd
            }
            title={Locale.get("reporting.tpdCoverage")}
          />
          <GrossCoverageCard
            order={3}
            data={
              reportingDetails.grossInsNeeds &&
              reportingDetails.grossInsNeeds.ci
            }
            title={Locale.get("reporting.ciCoverage")}
          />
        </Row>
      </SectionContainer>
      <Title marginBottom="24px">
        {Locale.get("reporting.sectionContainerTitle_0")}
      </Title>
      <SummaryInfoCard
        name={personalInfo.name}
        age={personalInfo.age}
        dob={personalInfo.dob}
        occupation={personalInfo.occupation}
        dependentYears={personalInfo.minYearsOfDependency}
        noOfDependentFamilyMember={personalInfo.noOfDependentFamilyMember}
      />
      <SectionContainer>
        <Title marginBottom="8px">
          {Locale.get("reporting.sectionContainerTitle_1")}
        </Title>
        <Row>
          <Table
            dataSource={naturalDeathCoverageData}
            columns={getRiskRatioTableColumns(
              Locale.get("reporting.naturalDeathCoverage")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
        <Row>
          <Table
            dataSource={accidentalDeathCoverageData}
            columns={getRiskRatioTableColumns(
              Locale.get("reporting.accidentalDeathCover")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
        <Row>
          <Table
            dataSource={medicalBenefitCoverageData}
            columns={getRiskRatioTableColumns(
              Locale.get("reporting.criticalIllnessMedBenefitCover")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
        <Row>
          <Table
            dataSource={tpdCoverageData}
            columns={getRiskRatioTableColumns(
              Locale.get("reporting.totalPermanentDisabilityCover")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
        <Row>
          <Table
            dataSource={comatoseCoverageData}
            columns={getRiskRatioTableColumns(
              Locale.get("reporting.comatoseCoverage")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
      </SectionContainer>
    </>
  );
};

Reporting.defaultProps = {
  personalInfo: {},
  financialInfo: {},
  insurancesInfo: {},
  reportingDetails: {},
  personalDetails: {},
  cashFlowDetails: {},
  estatePlanningDetails: {},
  investmentPortfolio: {},
  actionPlans: [],
  futureReviews: [],
};

export default Reporting;
